/**
 * The bell's own small panel.
 *
 * Pressing the bell should tell you what is waiting without taking you off
 * whatever you were doing - most of the time the answer is "nothing" or "one
 * friend request", and neither is worth a whole screen. What does not fit
 * goes behind "See all", which is the screen.
 *
 * It used to build its own list out of invites and friend requests, which is
 * why a mention or a message never appeared in it. It reads the notification
 * feed now, the same one the screen reads.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { go } from '@/lib/hash';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import {
  clearNotifications, markRead, useNotifications, type Notification
} from '@/lib/notifications';
import { NotificationRow } from './NotificationRow';

/** How many fit before it is a list rather than a glance. */
const PEEK = 6;

export function NotificationPeek({ anchor, onClose }: {
  anchor: HTMLElement | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  /*
   * Where it sits, always inside the viewport.
   *
   * It used to be placed by its right edge alone, at a fixed 372px wide.
   * Nothing stopped the left edge going negative, so on a 390px phone the
   * heading and avatars were cut off - and from the new left-hand sidebar the
   * same maths pushed it almost entirely off-screen.
   */
  const [spot, setSpot] = useState<
    | { sheet: false; left: number; top: number; width: number; maxHeight: number }
    | { sheet: true }
    | null
  >(null);

  const q = useNotifications();
  const all = q.data?.notifications ?? [];
  const unread = q.data?.unread ?? 0;
  const shown = all.slice(0, PEEK);

  useEffect(() => {
    const place = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const M = 12;

      /* Phones get a sheet from the bottom: full width with margins, and
         nowhere for an edge to fall off. */
      if (vw <= 640) { setSpot({ sheet: true }); return; }

      const width = Math.min(372, vw - M * 2);
      const a = anchor?.getBoundingClientRect();
      /* Hang from whichever edge of the bell faces into the screen: its right
         edge when it is on the right, its left edge in the sidebar. */
      let left = !a ? vw - width - M : a.left < vw / 2 ? a.left : a.right - width;
      left = Math.max(M, Math.min(left, vw - width - M));
      const top = a ? Math.min(a.bottom + 8, vh - 200) : 56;
      setSpot({ sheet: false, left, top, width, maxHeight: vh - top - M });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchor]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /* Opening one reads it and goes where it points. Both, always: a
     notification you have acted on is not still waiting for you. */
  function open(n: Notification) {
    void markRead(queryClient, [n.id]);
    onClose();
    if (n.route) go(n.route);
  }

  if (!spot) return null;

  return createPortal(
    <>
      <span className="fixed inset-0 z-[460]" onClick={onClose} />

      <div
        data-keeps-card
        role="dialog"
        aria-label="Notifications"
        style={spot.sheet
          ? { left: 12, right: 12, bottom: 'calc(74px + env(safe-area-inset-bottom, 0px))', maxHeight: 'calc(100dvh - 140px)' }
          : { left: spot.left, top: spot.top, width: spot.width, maxHeight: spot.maxHeight }}
        className="fixed z-[470] overflow-y-auto overscroll-contain rounded-[calc(14px*var(--radius-mul,1))] border border-line bg-surface-2 shadow-[var(--shadow-3)] animate-[rise_var(--t-menu)_var(--ease)]"
      >
        <header className="flex items-center gap-2 border-b border-line-soft px-3.5 py-2.5">
          <span className="text-[13.5px] font-bold">Notifications</span>
          {unread ? (
            <span className="rounded-full bg-accent px-1.5 text-[10px] font-extrabold text-accent-fg tabular-nums">
              {unread > 99 ? '99+' : unread}
            </span>
          ) : null}
          <span className="flex-1" />
          {unread ? (
            <button
              type="button"
              onClick={() => void markRead(queryClient)}
              className="text-[11.5px] font-semibold text-mute transition-colors hover:text-accent-hi"
            >
              Mark all read
            </button>
          ) : null}
        </header>

        {shown.length ? (
          <div className="flex max-h-[360px] flex-col gap-0.5 overflow-y-auto p-1.5">
            {shown.map((n) => (
              <NotificationRow key={n.id} n={n} compact onOpen={open} />
            ))}
          </div>
        ) : (
          <div className="px-4 py-8 text-center">
            <span className="mx-auto mb-2 grid h-9 w-9 place-items-center rounded-full bg-surface-3 text-mute">
              <Icon name="bell" size={16} />
            </span>
            <p className="text-[13px] font-semibold">Nothing waiting</p>
            <p className="mt-0.5 text-[12px] text-mute">
              Mentions, invites and requests land here.
            </p>
          </div>
        )}

        <footer className="flex items-center border-t border-line-soft">
          <button
            type="button"
            onClick={() => { onClose(); go('notifications'); }}
            className={cn(
              'flex-1 py-2.5 text-[12.5px] font-semibold text-accent-hi transition-colors hover:bg-surface-3'
            )}
          >
            See all{all.length > PEEK ? ' ' + all.length : ''}
          </button>
          {all.length ? (
            <button
              type="button"
              onClick={() => void clearNotifications(queryClient)}
              className="border-l border-line-soft px-3.5 py-2.5 text-[12.5px] font-semibold text-mute transition-colors hover:bg-surface-3 hover:text-bad"
            >
              Clear
            </button>
          ) : null}
        </footer>
      </div>
    </>,
    document.body
  );
}

/**
 * The bell itself.
 *
 * Counts unread notifications rather than being handed a number, because
 * everything that used to be counted separately - invites, friend requests -
 * now raises a notification too. One source, so the badge and the panel can
 * never disagree about how much is waiting.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [el, setEl] = useState<HTMLButtonElement | null>(null);
  const q = useNotifications();

  /*
   * Unread notifications, and nothing else.
   *
   * This used to take the larger of that and a count of pending invites and
   * friend requests, which are a different thing: a request stays pending
   * after you have read the notification about it. The two numbers drifted
   * apart the moment anyone cleared their list, and the bell showed a badge
   * over a panel that said nothing was waiting. Anything worth a badge raises
   * a notification, so the notification is the only thing counted.
   */
  const waiting = q.data?.unread ?? 0;

  return (
    <>
      <button
        type="button"
        ref={setEl}
        onClick={() => setOpen((v) => !v)}
        aria-label={waiting ? waiting + ' notifications' : 'Notifications'}
        title="Notifications"
        className={cn(
          'no-drag relative grid h-11 w-11 flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] border transition-[background-color,border-color,color] duration-[var(--t-hover)]',
          open ? 'border-accent-line bg-accent-soft text-accent-hi' : 'border-line-soft bg-surface-1 text-mute hover:border-line hover:text-accent-hi'
        )}
      >
        <Icon name="bell" size={18} />

        {waiting ? (
          <span
            className="absolute top-[3px] right-[2px] grid h-[16px] min-w-[16px] place-items-center rounded-full bg-bad px-1 text-[9.5px] font-extrabold text-white tabular-nums ring-2 ring-[var(--bg-0)]"
          >
            {waiting > 9 ? '9+' : waiting}
          </span>
        ) : null}
      </button>

      {open ? <NotificationPeek anchor={el} onClose={() => setOpen(false)} /> : null}
    </>
  );
}
